import { useQuery, type QueryKey } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { previewExportTemplate } from '../services/export.service';
import type { ExportTemplatePreview, ExportTemplatePreviewRequest } from '../types/export.types';

/** Products rendered in the preview. The server bounds this to 1–50. */
export const PREVIEW_ROW_COUNT = 5;

/**
 * A preview read other than the batch run's — the catalog export's
 * `POST /catalog/preview`, whose response is the template preview plus row
 * provenance. Lets `ExportPreview` render any source with one UI.
 */
export interface ExportPreviewFetcher {
  queryKey: (rows: number) => QueryKey;
  fetch: (rows: number) => Promise<ExportTemplatePreview>;
}

/** What the preview reads: a batch run's request, or a custom fetcher. */
export type ExportPreviewSource =
  | { request: ExportTemplatePreviewRequest; fetcher?: undefined }
  | { request?: undefined; fetcher: ExportPreviewFetcher };

type PreviewArgs = ExportPreviewSource & {
  /** False while the preview isn't visible — the request reads the enrichment
   *  table, so a closed preview must not pay for it. */
  enabled: boolean;
  rows?: number;
};

/**
 * Render the chosen template against the run's first products, raw and encoded.
 *
 * A query rather than a mutation so flipping between templates re-uses an
 * already-rendered answer instead of re-reading the table, and so React Query
 * owns the stale-response and unmount handling. Keyed on everything that
 * changes the cells (run, template, columns, filters, row count) — `format` is
 * excluded because the endpoint ignores it.
 *
 * `staleTime: Infinity`: the underlying enrichment rows are immutable once a
 * run has completed, so re-reading them within a session cannot change.
 */
export function useExportTemplatePreview({
  request,
  fetcher,
  enabled,
  rows = PREVIEW_ROW_COUNT,
}: PreviewArgs) {
  return useQuery({
    queryKey: fetcher
      ? fetcher.queryKey(rows)
      : queryKeys.exports.templatePreview(
          request.sourceWorkflowId,
          request.template ?? null,
          request.fields ?? null,
          request.filters ?? null,
          rows,
        ),
    queryFn: () => (fetcher ? fetcher.fetch(rows) : previewExportTemplate({ ...request, rows })),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
